import {
	copyChecklists,
	getChecklists,
	deleteChecklists,
} from 'api/checklists/checklistsAPI';
import useNotificationProvider from 'hooks/notificationBar/useNotificationProvider';
import { useEffect, useState } from 'react';

const useChecklistTable = (
	customerID,
	addressID,
	checklistType,
	searchInput,
) => {
	const [checklists, setChecklists] = useState([]);
	const [searchedChecklists, setSearchedChecklists] = useState([]);

	const { notifySuccess, notifyError, notifyWarning } =
		useNotificationProvider();

	const [pending, setPending] = useState(true);
	const [deleteAlert, setDeleteAlert] = useState(false);
	const [error, setError] = useState(false);

	const [selectAll, setSelectAll] = useState(false);

	//--------PAGINATION STATES START----------------//
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(5);

	const handleChangeRowsPerPage = (e) => {
		setRowsPerPage(parseInt(e.target.value, 10));
		setPage(0);
	};
	//--------PAGINATION STATES END----------------//
	//SEARCH FILTER
	useEffect(() => {
		const filtered = checklists.filter((list) => {
			return (
				list.Name?.toLowerCase().includes(searchInput?.toLowerCase()) ||
				list.Description?.toLowerCase().includes(
					searchInput?.toLowerCase(),
				)
			);
		});
		setSearchedChecklists(filtered);
	}, [searchInput, checklists]);

	useEffect(() => {
		fetchChecklists();
	}, [customerID, addressID, checklistType]);

	//-----------------HELPER FUNCTIONS START------------------//
	async function fetchChecklists() {
		setPending(true);
		try {
			let tempChecklists = [];
			if (customerID && addressID) {
				tempChecklists = await getChecklists(
					checklistType,
					customerID,
					addressID,
				); //get location checklists
			} else if (customerID === '0') {
				tempChecklists = await getChecklists(checklistType); //get global checklists
			} else {
				setChecklists([]);
			}
			setChecklists(tempChecklists);
		} catch (error) {
			notifyError('Error Fetching checklists!');
			setError(true);
		} finally {
			setPending(false);
		}
	}

	const deleteSelectedChecklists = async () => {
		setDeleteAlert(false);
		setPending(true);

		notifyWarning('Deleting');

		var _selectedChecklistIDs = checklists
			.filter((checklist) => checklist.isSelected)
			.map((checklist) => checklist.ChecklistID);

		try {
			await deleteChecklists(_selectedChecklistIDs);
		} catch (error) {
			notifyError('Error deleting checklist!');
			setPending(false);
			return;
		}

		setChecklists(checklists.filter((checklist) => !checklist.isSelected));

		setSelectAll(false);
		setPending(false);
		notifySuccess('Saved');
	};
	//-----------------HELPER FUNCTIONS END------------------//

	//-----------------HANDLERS START------------------//
	const onSelectChecklistID = (checklistID) => {
		const tempChecklists = checklists.map((checklist) => {
			if (checklist.ChecklistID === checklistID) {
				return {
					...checklist,
					isSelected: !(checklist.isSelected || false),
				};
			}
			return checklist;
		});
		setChecklists(tempChecklists);
	};

	const onSelectAllChecklists = () => {
		const tempChecklists = checklists.map((checklist) => ({
			...checklist,
			isSelected: !selectAll,
		}));
		setChecklists(tempChecklists);
		setSelectAll(!selectAll);
	};

	const onCopyChecklistClick = async () => {
		var _selectedChecklistIDs = checklists
			.filter((checklist) => checklist.isSelected)
			.map((checklist) => checklist.ChecklistID);

		if (_selectedChecklistIDs.length === 0) {
			notifyError('Please select checklist(s) to copy.');
			return;
		}

		notifyWarning('Copying');
		setPending(true);
		try {
			await copyChecklists(_selectedChecklistIDs);
		} catch (error) {
			notifyError('ERROR! COPYING CHECKLIST FAILED');
			setPending(false);
			return;
		}

		await fetchChecklists();

		setPending(false);
		notifySuccess('COPIED');
	};

	const onDeleteChecklistClick = () => {
		if (checklists.filter((checklist) => checklist.isSelected).length > 0) {
			setDeleteAlert(true);
		} else {
			notifyError('Please select checklist(s) to delete.');
		}
	};

	const deleteAlertHandlers = {
		onConfirm: () => deleteSelectedChecklists(),
		onDiscard: () => {
			setDeleteAlert(false);
		},
	};
	//-----------------HANDLERS END------------------//

	return {
		checklists,
		setChecklists,
		searchedChecklists,
		selectAll,
		onSelectAllChecklists,
		pending,
		deleteAlert,
		setDeleteAlert,
		onSelectChecklistID,
		onCopyChecklistClick,
		onDeleteChecklistClick,
		deleteAlertHandlers,
		page,
		setPage,
		rowsPerPage,
		handleChangeRowsPerPage,
		error,
	};
};

export default useChecklistTable;
